<script setup lang="ts">
import { i18n } from '@/i18n';
import { computed, ref, useId, watch } from 'vue';
import { ChartData, Chart as ChartJS, ChartOptions, registerables } from 'chart.js';
import { Line } from 'vue-chartjs';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { DimensionValueGroup } from '@/inventoryDashboardModule/domains/entities/Dashboard';
import { IYearMonth } from '@/coreDomainsModule/date';
import { UNASSIGNED_COLUMN } from '@/coreDomainsModule/constants';

const t = i18n.t;

const chartId = useId();
ChartJS.register(...registerables);

const props = defineProps<{ dimensionValueGroups: DimensionValueGroup[]; fromYearMonth: IYearMonth; toYearMonth: IYearMonth }>();
const chartRef = ref<{ chart: ChartJS } | null>(null);
const hiddenDatasetIds = ref<Set<string>>(new Set());

const periodLength = computed(() => DateService.calculateIYearMonthsDiff(props.fromYearMonth, props.toYearMonth, true));

const startMonthDiff = computed(() =>
  DateService.calculateIYearMonthsDiff(props.fromYearMonth, DateService.getShiftedIYearMonthFromNowDateByMonths(0))
);

const datasetIds = computed(() => props.dimensionValueGroups.map((dvg) => generateDatasetId(dvg)));

const datasets = computed(() =>
  props.dimensionValueGroups.map((dvg) => {
    const datasetId = generateDatasetId(dvg);
    return {
      id: datasetId,
      label: dvg.dimensionValues.name || t(UNASSIGNED_COLUMN),
      data: createData(dvg),
      hidden: hiddenDatasetIds.value.has(datasetId),
      segment: {
        // eslint-disable-next-line jsdoc/require-jsdoc
        borderDash: (ctx) => {
          const x = ctx.p0.parsed.x;

          if (x >= Math.abs(startMonthDiff.value) - 1) {
            return [4, 4];
          }
          return undefined;
        },
      },
    };
  })
);

const chartData = computed(
  (): ChartData<'line', { x: number; y: number }[]> => ({
    datasets: datasets.value,
  })
);

const chartOptions = computed<ChartOptions<'line'>>(() => ({
  responsive: true,
  maintainAspectRatio: false,
  aspectRatio: 0.6,
  parsing: false,
  scales: {
    x: {
      type: 'linear',
      min: 0,
      max: periodLength.value,
      ticks: {
        stepSize: 1,
        // eslint-disable-next-line jsdoc/require-jsdoc
        callback: (value) => {
          const v = Number(value);
          if (!Number.isInteger(v)) return '';
          return DateService.convertIYearMonthToYYYYMMWithSlash(
            DateService.getShiftedIYearMonthFromNowDateByMonths(startMonthDiff.value + v)
          );
        },
      },
    },
    y: {
      title: {
        display: true,
        text: t('達成率 (%)'),
      },
    },
  },
  interaction: {
    mode: 'nearest',
    axis: 'x',
    intersect: false,
  },
  hover: {
    mode: 'nearest',
    axis: 'x',
    intersect: false,
  },
  plugins: {
    legend: { display: true, position: 'bottom' },
    tooltip: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
      callbacks: {
        /**
         * ツールチップの数値を小数1桁目まで確実に表示する
         * @param ctx - Tooltipのobj
         */
        title: (ctx) => {
          const date = DateService.getShiftedIYearMonthFromNowDateByMonths(startMonthDiff.value + ctx[0].parsed.x);
          return DateService.convertIYearMonthToYYYYMMWithSlash(date);
        },
        /**
         * ツールチップの数値を小数1桁目まで確実に表示する
         * @param ctx - Tooltipのobj
         */
        label: (ctx) => {
          const v = ctx.parsed.y;
          if (v == null) return '';
          const label =
            ctx.dataset.label === t('実績') ? (ctx.parsed.x >= startMonthDiff.value ? t('予測') : t('実績')) : ctx.dataset.label;
          return `${label}: ${v.toFixed(1)}%`;
        },
      },
    },
  },
}));

/**
 * データセットのIDを生成する
 * @param dvg - DimensionValueGroup
 */
const generateDatasetId = (dvg: DimensionValueGroup) =>
  `${dvg.dimensionValues.code ?? ''}${dvg.dimensionValues.name ?? ''}:${dvg.dimensionValues.value ?? ''}:${dvg.dimensionValues.unit ?? ''}`;

/**
 * indexに対応するデータセットIDを取得する
 * @param datasetIndex - データセットのindex
 */
const getDatasetIdByIndex = (datasetIndex: number) => datasets.value[datasetIndex]?.id;

/**
 * KPI達成率グラフ用に、年月ごとの達成率データを生成する
 * @param dimensionValueGroup - 切り口ごとの表示データ
 */
const createData = (dimensionValueGroup: DimensionValueGroup) => {
  const res: { x: number; y: number }[] = [];
  for (let i = 0; i < periodLength.value + 1; i++) {
    const targetYearMonth = DateService.getShiftedIYearMonthFromNowDateByMonths(startMonthDiff.value + i);
    const targetDate = DateService.convertIYearMonthToLastDateIDate(targetYearMonth);
    const targetKpiArchvementData = dimensionValueGroup.amounts.find(
      (a) => DateService.compareYearMonths(a.yearMonth, targetYearMonth) === 0
    );
    if (!targetKpiArchvementData || !targetKpiArchvementData.turnover) continue;
    const targetKpi = dimensionValueGroup.kpi.find(
      (k) =>
        (!k.valid_from || DateService.compareDates(k.valid_from, targetDate) !== 1) &&
        (!k.valid_to || DateService.compareDates(k.valid_to, targetDate) !== -1)
    );
    if (!targetKpi) continue;
    res.push({ x: i, y: (targetKpi.kpiValue / targetKpiArchvementData.turnover) * 100 });
  }
  return res;
};

/**
 * データセットが表示中かどうかを返す
 * @param datasetIndex - データセットのindex
 */
const isDatasetVisible = (datasetIndex: number) => {
  const datasetId = getDatasetIdByIndex(datasetIndex);
  return datasetId ? !hiddenDatasetIds.value.has(datasetId) : true;
};

/**
 * 現在のデータセットに存在しない非表示IDを削除する
 */
const cleanupHiddenDatasetIds = () => {
  const currentDatasetIds = new Set(datasetIds.value);
  const nextHiddenDatasetIds = new Set([...hiddenDatasetIds.value].filter((datasetId) => currentDatasetIds.has(datasetId)));

  if (nextHiddenDatasetIds.size === hiddenDatasetIds.value.size) return;

  hiddenDatasetIds.value = nextHiddenDatasetIds;
};

/**
 * データセットの表示非表示を切り替える
 * @param datasetIndex - 切り替えるデータセットのindex
 */
const toggleDataset = (datasetIndex: number) => {
  const datasetId = getDatasetIdByIndex(datasetIndex);
  if (!datasetId) return;

  const nextHiddenDatasetIds = new Set(hiddenDatasetIds.value);

  if (nextHiddenDatasetIds.has(datasetId)) {
    nextHiddenDatasetIds.delete(datasetId);
  } else {
    nextHiddenDatasetIds.add(datasetId);
  }

  hiddenDatasetIds.value = nextHiddenDatasetIds;
};

watch(datasetIds, () => cleanupHiddenDatasetIds(), { flush: 'post', immediate: true });

defineExpose({ toggleDataset, isDatasetVisible });
</script>
<template>
  <Line :id="chartId" ref="chartRef" dataset-id-key="id" :data="chartData" :options="chartOptions" />
</template>
<style lang="scss" scoped></style>
